import React, { useState, useEffect, useRef, useCallback } from 'react';
import CoverRenderer from '../Storybook/CoverRenderer';
 
interface PreviewProps {
  storyTitle: string;
  authorName: string;
  coverData: any;
  chapters: any[];
  onClose: () => void;
}
 
// ─── Blurb 5×8 coordinate system ─────────────────────────────────────────────
// Trim:  360 × 576 pts (5 × 8 in at 72pt/in)  →  our px base: 500 × 800
// Scale: 500/360 = 1.389  |  Safe margins: 18pt outer → 25px, 36pt binding → 50px
const PAGE_W = 500;
const PAGE_H = 800;
 
// Blurb-spec safe margins in our px coordinate system
const SAFE_OUTER   = 25;   // top, bottom, outside edge
const SAFE_BINDING = 50;   // binding (left) edge
 
// Blurb PDF dimensions in points (used by jsPDF)
const BLURB_PT_W = 360;
const BLURB_PT_H = 576;
 
interface Page {
  type: 'cover' | 'title' | 'chapter-title' | 'content' | 'image';
  content?: React.ReactNode;
}
 
// ─── Interior page shell with Blurb-correct margins ──────────────────────────
const InteriorPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    width: PAGE_W, height: PAGE_H, background: 'white',
    position: 'relative', overflow: 'hidden', boxSizing: 'border-box',
    paddingLeft: SAFE_BINDING, paddingRight: SAFE_OUTER,
    paddingTop: SAFE_OUTER, paddingBottom: SAFE_OUTER,
  }}>
    {children}
  </div>
);
 
// ─── Upload guide modal ───────────────────────────────────────────────────────
const UploadGuide: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 20000,
    background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
  }}>
    <div style={{
      background: '#fff', borderRadius: 20, padding: '36px 40px', maxWidth: 480, width: '100%',
      boxShadow: '0 32px 80px rgba(0,0,0,0.4)',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 900, color: '#1a3a52', fontFamily: "'Epilogue', sans-serif" }}>
        Your PDF is ready!
      </h2>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: '#525c6a', lineHeight: 1.6 }}>
        <strong>"{title}"</strong> has downloaded. Follow these 3 steps to print with Blurb:
      </p>
 
      {[
        { n: '1', label: 'Create a Blurb account', sub: 'Free at blurb.com' },
        { n: '2', label: 'Start a new Trade Book (5×8)', sub: 'Choose "PDF to Book" › Upload PDF' },
        { n: '3', label: 'Upload your PDF & order', sub: 'Economy softcover starts ~$4–6' },
      ].map(s => (
        <div key={s.n} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: '#6a37d4', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 900, fontSize: 14,
          }}>{s.n}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#252f3c' }}>{s.label}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{s.sub}</div>
          </div>
        </div>
      ))}
 
      <div style={{ display: 'flex', gap: 10, marginTop: 28 }}>
        <a
          href="https://www.blurb.com/hardcover-and-paperback-books/paperback-book"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1, padding: '13px 0', borderRadius: 9999, textAlign: 'center',
            background: 'linear-gradient(135deg, #6a37d4, #ae8dff)',
            color: '#fff', fontWeight: 700, fontSize: 15, textDecoration: 'none',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          Open Blurb →
        </a>
        <button onClick={onClose} style={{
          padding: '13px 20px', borderRadius: 9999, border: '2px solid #dce9fd',
          background: 'transparent', color: '#525c6a', fontWeight: 700, fontSize: 14,
          cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>
          Done
        </button>
      </div>
 
      <p style={{ margin: '16px 0 0', fontSize: 11, color: '#aaa', textAlign: 'center', lineHeight: 1.5 }}>
        💡 Tip: Your PDF is sized to Blurb's 5×8 trim spec. Upload it directly — no resizing needed.
      </p>
    </div>
  </div>
);
 
// ─── Main component ───────────────────────────────────────────────────────────
const StoryPreview: React.FC<PreviewProps> = ({ storyTitle, authorName, coverData, chapters, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [scale, setScale]             = useState(1);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);
 
  // Export state
  const [exporting, setExporting]       = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [showGuide, setShowGuide]       = useState(false);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [editingAuthor, setEditingAuthor]     = useState(false);
const [localAuthorName, setLocalAuthorName] = useState(authorName);
 
  // ── Viewport scale ──────────────────────────────────────────────────────
  useEffect(() => {
    const recalc = () => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const mobile = vw < 768;
        setIsMobile(mobile);
        const availW = vw - 80;
        const isCover = currentPage === 0;
        const availH = vh - (isCover ? 240 : 180); // ← more offset for cover
        if (mobile) {
            setScale(Math.min(availW / PAGE_W, availH / PAGE_H, 1));
        } else {
            const spreadW = isCover ? PAGE_W : PAGE_W * 2 + 4;
            setScale(Math.min(availW / spreadW, availH / PAGE_H, 1));
        }
    };
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
}, [currentPage]); // ← add currentPage so it recalculates on page turn
 
  // ── Build pages ─────────────────────────────────────────────────────────
  const buildPages = useCallback((): Page[] => {
    const pages: Page[] = [];
 
    if (coverData) {
      pages.push({
        type: 'cover',
        content: <CoverRenderer coverData={coverData} width={PAGE_W} />,
      });
    }
 
    pages.push({
      type: 'title',
      content: (
        <InteriorPage>
          <div style={{
            position: 'absolute',
            left: SAFE_BINDING, right: SAFE_OUTER, top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', textAlign: 'center', gap: 16,
          }}>
            <h1 style={{
              fontSize: storyTitle.length > 30 ? 26 : storyTitle.length > 20 ? 32 : 38,
              fontWeight: 900, color: '#1a3a52', lineHeight: 1.3, margin: 0,
              letterSpacing: -0.5, fontFamily: 'Georgia, serif', wordBreak: 'break-word',
            }}>
              {storyTitle}
            </h1>
            <div style={{ width: 40, height: 2, background: '#6C5CE7', borderRadius: 2 }} />
            <div style={{ fontSize: 16, color: '#888', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>
            by {localAuthorName || 'Author'}
            </div>
          </div>
          <div style={{
            position: 'absolute', bottom: SAFE_OUTER, left: SAFE_BINDING, right: SAFE_OUTER,
            textAlign: 'center', fontSize: 11, color: '#CCC',
            letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Georgia, serif',
          }}>
            Written in Scriblish
          </div>
        </InteriorPage>
      ),
    });
 
    chapters.forEach(chapter => {
      pages.push({
        type: 'chapter-title',
        content: (
          <InteriorPage>
            <div style={{
              position: 'absolute', left: SAFE_BINDING, right: SAFE_OUTER, top: '30%',
              transform: 'translateY(-50%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16,
            }}>
              <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: '#6C5CE7', fontFamily: 'Georgia, serif' }}>
                Chapter
              </div>
              <h2 style={{
                fontSize: chapter.title.length > 30 ? 22 : 28,
                fontWeight: 800, color: '#1a3a52', margin: 0, lineHeight: 1.3,
                fontFamily: 'Georgia, serif', wordBreak: 'break-word',
              }}>
                {chapter.title}
              </h2>
              <div style={{ width: 48, height: 3, background: '#E0E0E0', borderRadius: 2 }} />
            </div>
          </InteriorPage>
        ),
      });
 
      chapter.blocks?.forEach((block: any) => {
        if (block.type === 'text' && block.content) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(block.content, 'text/html');
          const paragraphs = Array.from(doc.body.children);
          const chunks: string[] = [];
          let chunk: string[] = [], len = 0;
          paragraphs.forEach(p => {
            const text = p.textContent || '';
            if (!text.trim()) return;
            if (len + text.length > 900 && chunk.length > 0) {
              chunks.push(chunk.join(''));
              chunk = [p.outerHTML]; len = text.length;
            } else { chunk.push(p.outerHTML); len += text.length; }
          });
          if (chunk.length > 0) chunks.push(chunk.join(''));
 
          chunks.forEach(c => pages.push({
            type: 'content',
            content: (
              <InteriorPage>
                <div style={{ fontSize: 14, lineHeight: 1.9, color: '#222', fontFamily: 'Georgia, serif' }}
                  dangerouslySetInnerHTML={{ __html: c }} />
              </InteriorPage>
            ),
          }));
        }
 
        if (block.type === 'image' && block.imageUrl) {
          pages.push({
            type: 'image',
            content: (
              <InteriorPage>
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                  <img src={block.imageUrl} alt={block.caption || ''} style={{
                    maxWidth: '100%', maxHeight: block.caption ? '85%' : '100%',
                    height: 'auto', borderRadius: 4, boxShadow: '0 2px 16px rgba(0,0,0,0.1)', objectFit: 'contain',
                  }} />
                  {block.caption && (
                    <p style={{ margin: 0, fontSize: 12, fontStyle: 'italic', color: '#888', textAlign: 'center', fontFamily: 'Georgia, serif' }}>
                      {block.caption}
                    </p>
                  )}
                </div>
              </InteriorPage>
            ),
          });
        }
      });
    });
 
// ── Credits / colophon ─────────────────────────────────────────────────
pages.push({
    type: 'content',
    content: (
        <InteriorPage>
            <div style={{
                position: 'absolute',
                left: SAFE_BINDING, right: SAFE_OUTER,
                top: '50%', transform: 'translateY(-50%)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', textAlign: 'center', gap: 12,
            }}>
                {/* Decorative mark */}
                <div style={{ fontSize: 32, marginBottom: 4 }}>✦</div>

                <div style={{ width: 40, height: 2, background: '#e0e0e0', borderRadius: 2 }} />

                <p style={{ margin: 0, fontSize: 13, color: '#888', fontFamily: 'Georgia, serif', fontStyle: 'italic', lineHeight: 1.8 }}>
                    <strong style={{ fontStyle: 'normal', color: '#1a3a52' }}>{localAuthorName || 'This author'}</strong>
                    {' '}wrote this book using{' '}
                    <strong style={{ fontStyle: 'normal', color: '#6a37d4' }}>Scriblish</strong>
                    {' '}— a creative writing platform for young writers.
                </p>

                <div style={{ width: 40, height: 2, background: '#e0e0e0', borderRadius: 2 }} />

                <p style={{ margin: 0, fontSize: 11, color: '#bbb', fontFamily: 'Georgia, serif', letterSpacing: 1 }}>
                    First written {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                </p>

                <p style={{ margin: 0, fontSize: 11, color: '#ccc', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                    scriblish.com
                </p>
            </div>
        </InteriorPage>
    ),
});

    return pages;
}, [storyTitle, coverData, chapters, localAuthorName]);
 
  const pages = buildPages();
  const total = pages.length;
  pageRefs.current = new Array(total).fill(null);
 
  // ── PDF export ──────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    setExportProgress(0);
 
    try {
      // Dynamic imports — only loaded when user clicks export
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
 
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [BLURB_PT_W, BLURB_PT_H],
        compress: true,
      });
 
      for (let i = 0; i < pageRefs.current.length; i++) {
        const el = pageRefs.current[i];
        if (!el) continue;
 
        const canvas = await html2canvas(el, {
          scale: 3,               // 300 DPI (500px × 3 = 1500px = 300dpi at 5in)
          useCORS: true,          // allow external images (graphics, backgrounds)
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
        });
 
        if (i > 0) pdf.addPage([BLURB_PT_W, BLURB_PT_H], 'portrait');
        pdf.addImage(
          canvas.toDataURL('image/jpeg', 0.92),
          'JPEG', 0, 0, BLURB_PT_W, BLURB_PT_H,
          undefined, 'FAST'
        );
 
        setExportProgress(Math.round(((i + 1) / pageRefs.current.length) * 100));
      }
 
      const filename = `${storyTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-blurb.pdf`;
      pdf.save(filename);
      setShowGuide(true);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Export failed — please try again. Make sure html2canvas and jspdf are installed.');
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };
 
  // ── Spread / navigation ─────────────────────────────────────────────────
  const getSpread = () => {
    if (isMobile) return { left: null, right: pages[currentPage] };
    if (currentPage === 0) return { left: null, right: pages[0] };
    const li = currentPage % 2 === 1 ? currentPage : currentPage - 1;
    return { left: pages[li] ?? null, right: pages[li + 1] ?? null };
  };
 
  const goNext = () => {
    if (isMobile) { setCurrentPage(p => Math.min(total - 1, p + 1)); return; }
    if (currentPage === 0) { setCurrentPage(1); return; }
    const li = currentPage % 2 === 1 ? currentPage : currentPage - 1;
    setCurrentPage(Math.min(total - 1, li + 2));
  };
  const goPrev = () => {
    if (isMobile) { setCurrentPage(p => Math.max(0, p - 1)); return; }
    if (currentPage <= 1) { setCurrentPage(0); return; }
    const li = currentPage % 2 === 1 ? currentPage : currentPage - 1;
    setCurrentPage(Math.max(0, li - 2));
  };
 
  const { left, right } = getSpread();
  const showSingle = !left || currentPage === 0;
  const isFirst = currentPage === 0;
  const isLast  = isMobile
    ? currentPage === total - 1
    : currentPage >= (total % 2 === 0 ? total - 2 : total - 1);
 
  const spreadLayoutW = showSingle ? PAGE_W : PAGE_W * 2 + 4;
  const spreadVisualW = spreadLayoutW * scale;
  const spreadVisualH = PAGE_H * scale;
 
  const PageFrame: React.FC<{ page: Page | null; num: number; side: 'left' | 'right' }> = ({ page, num, side }) => (
    <div style={{
        width: PAGE_W, height: PAGE_H, position: 'relative',
        overflow: page?.type === 'cover' ? 'visible' : 'hidden',
        flexShrink: 0,
        background: page ? 'white' : '#FFFEF7',
        borderRadius: page?.type === 'cover' ? '4px' : 0, // ← subtle rounded corners on cover
        boxShadow: page?.type === 'cover'
            ? '0 8px 40px rgba(0,0,0,0.5)'
            : side === 'left'
            ? 'inset -4px 0 8px rgba(0,0,0,0.06)'
            : 'inset 4px 0 8px rgba(0,0,0,0.06)',
    }}>
        {page?.content}
        {page && page.type !== 'cover' && (
            <div style={{
                position: 'absolute', bottom: SAFE_OUTER,
                ...(side === 'left' ? { left: SAFE_BINDING } : { right: SAFE_OUTER }),
                fontSize: 11, color: '#BBB', fontFamily: 'Georgia, serif', letterSpacing: 1,
            }}>{num}</div>
        )}
    </div>
);
 
  return (
    <>
      <style>{`
        @keyframes previewFadeIn { from { opacity:0; transform:scale(0.98); } to { opacity:1; transform:scale(1); } }
        .preview-spread { animation: previewFadeIn 0.2s ease; }
        .preview-spread p { margin: 0 0 12px; }
        .preview-spread p:last-child { margin-bottom: 0; }
        .preview-spread strong { font-weight:700; }
        .preview-spread em { font-style:italic; }
        .preview-spread u { text-decoration:underline; }
      `}</style>
 
      {/* ── Hidden export container (off-screen, renders all pages at full size) ── */}
      <div style={{ position: 'fixed', left: -9999, top: 0, pointerEvents: 'none', zIndex: -1 }}>
        {pages.map((page, i) => (
          <div
            key={i}
            ref={el => { pageRefs.current[i] = el; }}
            style={{ width: PAGE_W, height: PAGE_H, overflow: 'hidden', background: 'white' }}
          >
            {page.content}
          </div>
        ))}
      </div>
 
      {/* ── Guide modal ── */}
      {showGuide && <UploadGuide title={storyTitle} onClose={() => setShowGuide(false)} />}
 
      {/* ── Main overlay ── */}
      <div style={{
    position: 'fixed', inset: 0, background: 'rgba(15,15,20,0.96)',
    zIndex: 10000, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'flex-start', // ← was 'center'
    padding: '20px 20px 20px', gap: 20,
    overflowY: 'auto',
}}>
        {/* Header */}
        <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    width: '100%', maxWidth: '100%', gap: 12, // ← was spreadVisualW
}}>
    {/* Left — title + author editor */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflow: 'hidden', minWidth: 0 }}>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Georgia, serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {storyTitle}
        </div>

        {/* Author name — prominent, clearly editable */}
        {editingAuthor ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                    autoFocus
                    value={localAuthorName}
                    onChange={e => setLocalAuthorName(e.target.value)}
                    onBlur={() => setEditingAuthor(false)}
                    onKeyDown={e => { if (e.key === 'Enter') setEditingAuthor(false); }}
                    placeholder="Your real name here"
                    style={{ background: 'rgba(255,255,255,0.15)', border: '2px solid #ae8dff', borderRadius: 8, padding: '6px 12px', color: '#fff', fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, outline: 'none', width: 220 }}
                />
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>↵ to save</span>
            </div>
        ) : (
            <button onClick={() => setEditingAuthor(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: 'rgba(174,141,255,0.18)', border: '1.5px dashed rgba(174,141,255,0.6)', borderRadius: 9999, cursor: 'pointer', transition: 'all 0.15s', width: 'fit-content' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(174,141,255,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(174,141,255,0.18)'; }}>
                <span style={{ fontSize: 14 }}>✏️</span>
                <span style={{ color: '#ae8dff', fontSize: 13, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {localAuthorName ? `by ${localAuthorName}` : 'Add your name for the PDF →'}
                </span>
            </button>
        )}
    </div>

    {/* Right — export + close */}
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
        <button onClick={handleExport} disabled={exporting}
            style={{ padding: '8px 18px', borderRadius: 9999, border: 'none', cursor: exporting ? 'wait' : 'pointer', background: exporting ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #6a37d4, #ae8dff)', color: exporting ? 'rgba(255,255,255,0.5)' : '#fff', fontWeight: 700, fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif", display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s', flexShrink: 0 }}>
            {exporting ? (
                <>
                    <span style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                    {exportProgress}%
                </>
            ) : '📄 Export for Blurb'}
        </button>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%', width: 36, height: 36, fontSize: 16, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
    </div>
</div>
 
        {/* Book spread */}
        {/* Book spread */}
<div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
    <div style={{
        width: spreadVisualW,
        height: spreadVisualH,
        flexShrink: 0,
        padding: '10px 20px',
        boxSizing: 'border-box' as const,
    }}>
        <div className="preview-spread" key={currentPage} style={{
            width: spreadLayoutW, height: PAGE_H,
            display: 'flex', borderRadius: 4, overflow: 'hidden',
            boxShadow: '0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
            transform: `scale(${scale})`, transformOrigin: 'top left',
            margin: currentPage === 0 ? '0 auto' : 0,
        }}>
            {showSingle ? (
                <PageFrame page={right} num={currentPage + 1} side="right" />
            ) : (
                <>
                    <PageFrame page={left} num={currentPage % 2 === 1 ? currentPage : currentPage - 1} side="left" />
                    <div style={{ width: 4, flexShrink: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.15), rgba(0,0,0,0.05))' }} />
                    <PageFrame page={right} num={(currentPage % 2 === 1 ? currentPage : currentPage - 1) + 1} side="right" />
                </>
            )}
        </div>
    </div>
</div>
 
        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <button onClick={goPrev} disabled={isFirst} style={{
            background: isFirst ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.15)', borderRadius: '50%',
            width: 44, height: 44, fontSize: 18, cursor: isFirst ? 'not-allowed' : 'pointer',
            color: isFirst ? 'rgba(255,255,255,0.2)' : 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>←</button>
 
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {Array.from({ length: Math.ceil(total / (isMobile ? 1 : 2)) }).map((_, i) => {
              const active = isMobile ? i === currentPage : i === Math.floor(currentPage / 2);
              return (
                <div key={i} onClick={() => setCurrentPage(isMobile ? i : i * 2)} style={{
                  width: active ? 20 : 6, height: 6, borderRadius: 3,
                  background: active ? 'white' : 'rgba(255,255,255,0.3)',
                  cursor: 'pointer', transition: 'all 0.2s ease',
                }} />
              );
            })}
          </div>
 
          <button onClick={goNext} disabled={isLast} style={{
            background: isLast ? 'rgba(255,255,255,0.08)' : 'white', border: 'none',
            borderRadius: '50%', width: 44, height: 44, fontSize: 18,
            cursor: isLast ? 'not-allowed' : 'pointer',
            color: isLast ? 'rgba(255,255,255,0.2)' : '#1a3a52',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isLast ? 'none' : '0 4px 12px rgba(0,0,0,0.3)',
          }}>→</button>
        </div>
 
        {/* Counter */}
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, letterSpacing: 1, fontFamily: 'Georgia, serif' }}>
          {currentPage + 1} of {total}
        </div>
      </div>
 
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};
 
export default StoryPreview;